import { Head, useForm } from '@inertiajs/react'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Card, Field, Label, Input, Select, Button, ErrorMessage, Text } from '@/components/ui'
import { AssignmentList } from '@/components/common/AssignmentList'
import { useTranslations } from '@/lib/i18n'

interface UserData {
  id: number | null
  name: string
  email: string
  role: string
  merchant_ids: number[]
}

interface UserFormProps {
  user: UserData
  roles: string[]
  assignable_merchants: { id: number; name: string }[]
  errors: Record<string, string>
}

function UserForm({ user, roles, assignable_merchants, errors }: UserFormProps) {
  const { t } = useTranslations()
  const isEdit = user.id !== null
  const form = useForm({
    name: user.name,
    email: user.email,
    role: user.role,
    merchant_ids: user.merchant_ids,
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ user: data }))
    if (isEdit) form.patch(`/users/${user.id}`)
    else form.post('/users')
  }

  function toggleMerchant(id: number, checked: boolean) {
    form.setData('merchant_ids', checked ? [...form.data.merchant_ids, id] : form.data.merchant_ids.filter((x) => x !== id))
  }

  const title = isEdit ? t('users.edit_title') : t('users.new_title')
  // operators/viewers are scoped to assigned merchants; admins have all access.
  const showMerchants = form.data.role === 'operator' || form.data.role === 'viewer'

  return (
    <>
      <Head title={title} />
      <PageHeading title={title} actions={<Button plain href="/users">{t('common.back')}</Button>} />

      <Card className="mt-8 max-w-2xl">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <Field>
            <Label>{t('users.name')}</Label>
            <Input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} autoFocus />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('users.email')}</Label>
            <Input
              type="email"
              value={form.data.email}
              onChange={(e) => form.setData('email', e.target.value)}
              disabled={isEdit}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('users.role')}</Label>
            <Select value={form.data.role} onChange={(e) => form.setData('role', e.target.value)}>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role}`)}
                </option>
              ))}
            </Select>
            {errors.role && <ErrorMessage>{errors.role}</ErrorMessage>}
          </Field>

          {showMerchants && (
            <Field>
              <Label>{t('users.assigned_merchants')}</Label>
              {assignable_merchants.length === 0 ? (
                <Text className="text-text/60">{t('merchants.empty')}</Text>
              ) : (
                <AssignmentList
                  items={assignable_merchants.map((m) => ({ id: m.id, label: m.name }))}
                  selected={form.data.merchant_ids}
                  onToggle={toggleMerchant}
                  searchPlaceholder={t('common.search')}
                />
              )}
            </Field>
          )}

          <div>
            <Button type="submit" color="brand" disabled={form.processing}>
              {isEdit ? t('common.save') : t('users.invite')}
            </Button>
          </div>
        </form>
      </Card>
    </>
  )
}

UserForm.layout = withAppLayout

export default UserForm
